import { FormInstance } from "antd";
import { format } from "date-fns";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";

import { OpportunityFormValues } from "./types";

type FormField = keyof OpportunityFormValues;

const FORM_FIELDS: FormField[] = [
  "opportunity_name",
  "opportunity_account",
  "opportunity_deal_size",
  "opportunity_close_date",
  "opportunity_stage",
  "opportunity_contact",
  "opportunity_other_stakeholders",
  "opportunity_next_step",
  "opportunity_owner",
  "opportunity_next_action_date",
];

const checkFormFields = (
  form: FormInstance<OpportunityFormValues> | null,
  fieldsToCheck: FormField[] = FORM_FIELDS,
): boolean => {
  if (!form) return false;
  return fieldsToCheck.some(field => form.getFieldValue(field));
};

/**
 * Check if the form has value without the pre-populated fields
 * 1. opportunity_stage
 * 2. opportunity_owner
 * @param form - The form instance
 * @returns true if the form has value without the pre-populated fields, false otherwise
 */
export const checkFormHasValueWithoutPrePopulatedFields = (
  form: FormInstance<OpportunityFormValues> | null,
): boolean => {
  const excludedFields = [
    SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_STAGE,
    SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OWNER,
  ];
  const fieldsWithoutStage = FORM_FIELDS.filter(
    field => !excludedFields.includes(field),
  );
  return checkFormFields(form, fieldsWithoutStage);
};
/**
 * Formats date values in an object to 'yyyy-MM-dd' string format.
 * Handles both single Date objects and arrays of Date objects.
 * Non-date values remain unchanged.
 *
 * @param {Record<string, unknown>} data - The input object containing potential date values
 * @returns {Record<string, unknown>} A new object with formatted date values
 *
 * @example
 * // Single date
 * formatDateValues({
 *   name: "John",
 *   birthDate: new Date("2024-03-20"),
 *   active: true
 * });
 * // Returns:
 * // {
 * //   name: "John",
 * //   birthDate: "2024-03-20",
 * //   active: true
 * // }
 *
 * @example
 * // Array of dates
 * formatDateValues({
 *   meetingDates: [
 *     new Date("2024-03-20"),
 *     new Date("2024-03-21")
 *   ],
 *   status: "pending"
 * });
 * // Returns:
 * // {
 * //   meetingDates: ["2024-03-20", "2024-03-21"],
 * //   status: "pending"
 * // }
 */
export const formatDateValues = (data: Record<string, unknown>) => {
  const formattedData = { ...data };

  Object.keys(formattedData).forEach(key => {
    const value = formattedData[key];

    // Check if value is an array of dates
    if (Array.isArray(value) && value.every(item => item instanceof Date)) {
      formattedData[key] = value.map(date => format(date, "yyyy-MM-dd"));
    }
    // Check if value is a single date
    else if (value instanceof Date) {
      formattedData[key] = format(value, "yyyy-MM-dd");
    }
  });

  return formattedData;
};
